import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Gift, Copy, Check, Share2, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api-client";

export default function GiftSuccess() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Gateway path: gift ID comes from the callback redirect query param.
  const giftId = new URLSearchParams(window.location.search).get("giftId");
  const { data: fetchedGift, isLoading } = useQuery({
    queryKey: ["/gifts", giftId],
    queryFn: async () => {
      const res = await apiClient.get(`/gifts/${giftId}`);
      if (!res.ok) throw new Error("Failed to load gift");
      return res.json();
    },
    enabled: !!giftId && !!apiClient.getToken(),
  });

  // Wallet path: gift data is in sessionStorage.
  const raw = sessionStorage.getItem("giftResult");
  const sessionData = raw ? JSON.parse(raw) : null;
  const data = giftId ? fetchedGift : sessionData;

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-24 bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen pt-24 pb-24 bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">{t("giftPage.noData")}</p>
          <Link href="/gifts"><Button className="mt-4">{t("giftPage.title")}</Button></Link>
        </div>
      </div>
    );
  }

  const codes: { code: string; serviceName?: string }[] = data.gifts
    ? data.gifts
    : [{ code: data.code, serviceName: data.serviceName }];

  const allCodes = codes.map((c) => c.code).join("\n");

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(key);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const whatsappText = isRtl
    ? `لقد أرسلت لك هدية من تايم كلين! ${codes.length > 1 ? "الأكواد:\n" + allCodes : "الرمز: " + codes[0].code}`
    : `I sent you a Time Clean gift! ${codes.length > 1 ? "Codes:\n" + allCodes : "Code: " + codes[0].code}`;

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background flex items-center justify-center" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-md">
        {/* Success icon */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-1">{t("giftPage.codeGenerated")}</h1>
          <p className="text-sm text-muted-foreground">{t("giftPage.shareInstruction")}</p>
        </div>

        {/* Code cards */}
        <div className="space-y-3 mb-6">
          {codes.map((g, i) => (
            <div key={i} className="bg-card rounded-2xl border border-border/60 p-5">
              {g.serviceName && (
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-primary" />
                  <p className="text-sm text-muted-foreground">{g.serviceName}</p>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xl font-bold tracking-[0.15em] text-primary break-all">
                  {g.code}
                </p>
                <button
                  type="button"
                  onClick={() => handleCopy(g.code, g.code)}
                  className="shrink-0 rounded-xl border border-border/60 p-2.5 hover:bg-muted/50 transition-colors"
                >
                  {copiedCode === g.code ? (
                    <Check className="h-5 w-5 text-green-500" />
                  ) : (
                    <Copy className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {codes.length > 1 && (
            <Button
              className="w-full h-12 rounded-xl"
              variant="outline"
              onClick={() => handleCopy(allCodes, "all")}
            >
              {copiedCode === "all" ? (
                <><Check className="h-4 w-4 me-2" />{t("giftPage.copied")}</>
              ) : (
                <><Copy className="h-4 w-4 me-2" />{t("giftPage.copyAll")}</>
              )}
            </Button>
          )}

          <Button className="w-full h-12 rounded-xl" asChild>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(whatsappText)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Share2 className="h-4 w-4 me-2" />
              {t("giftPage.shareWhatsapp")}
            </a>
          </Button>

          <Link href="/gifts" className="block">
            <Button variant="ghost" className="w-full h-12 rounded-xl">
              {t("giftPage.buyAnother")}
            </Button>
          </Link>

          <Link href="/" className="block">
            <Button variant="ghost" className="w-full h-12 rounded-xl">
              {t("giftPage.backHome")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
